// client/src/components/company/CompanyDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDuration, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { History, TrendingUp, Mail, PieChart as PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const CompanyDashboard = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const [activeRequests, setActiveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAuthenticated, token, initialized, user } = useAuth();

  // Analytics state
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalAllocated: 0,
    byBank: [],
    byDuration: []
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const hasAutoSwitchedCurrency = useRef(false);

  // Hot money analytics state
  const [selectedHotMoneyCurrency, setSelectedHotMoneyCurrency] = useState('EUR');
  const [availableHotMoneyCurrencies, setAvailableHotMoneyCurrencies] = useState([]);
  const [hotMoneyAnalytics, setHotMoneyAnalytics] = useState({
    totalAllocated: 0,
    byBank: [],
    byDuration: []
  });
  const [hotMoneyAnalyticsLoading, setHotMoneyAnalyticsLoading] = useState(false);
  const hasAutoSwitchedHotMoneyCurrency = useRef(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Make sure we don't try to fetch before authentication is initialized
      if (!initialized) {
        return;
      }

      // Reset states when starting a new fetch
      setLoading(true);
      setError('');

      try {
        // Make sure the token is set in axios headers
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        // Fetch all requests for the company (liquidity/hot money + VAT credit)
        const [requestsRes, vatCreditRes] = await Promise.all([
          axios.get('/api/requests'),
          axios.get('/api/vat-credit-requests')
        ]);

        // Get active requests
        const active = requestsRes.data.requests.filter(request =>
          ['bidding', 'pending_allocation'].includes(request.status)
        );

        // Get active VAT credit requests, tagged so they render with their own columns/badge
        const activeVatCredit = (vatCreditRes.data.requests || [])
          .filter(request => ['bidding', 'pending_allocation'].includes(request.status))
          .map(request => ({ ...request, requestKind: 'vat_credit' }));

        const combined = [...active, ...activeVatCredit].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setActiveRequests(combined);
        setLoading(false);
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        setError(t('common.errors.fetchDashboard', {
        defaultValue: 'Failed to fetch dashboard data. Please try again.'
        }));
        setLoading(false);
      }
    };

    // Only fetch data if authenticated and initialized
    if (isAuthenticated && initialized) {
      fetchDashboardData();
    }
  }, [isAuthenticated, token, initialized, t]);

  // Fetch analytics data when currency changes
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!isAuthenticated || !initialized) {
        return;
      }

      setAnalyticsLoading(true);
      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        const response = await axios.get('/api/requests/analytics/allocations', {
          params: { currency: selectedCurrency }
        });

        setAnalytics(response.data);

        if (response.data.availableCurrencies?.length > 0) {
          setAvailableCurrencies(response.data.availableCurrencies);
        }

        // Auto-switch to the first available currency on initial load if EUR has no data
        if (
          !hasAutoSwitchedCurrency.current &&
          response.data.totalAllocated === 0 &&
          response.data.availableCurrencies?.length > 0 &&
          !response.data.availableCurrencies.includes(selectedCurrency)
        ) {
          hasAutoSwitchedCurrency.current = true;
          setSelectedCurrency(response.data.availableCurrencies[0]);
        } else {
          hasAutoSwitchedCurrency.current = true;
        }

        setAnalyticsLoading(false);
      } catch (err) {
        console.error('Analytics fetch error:');
        setAnalyticsLoading(false);
      }
    };

    if (isAuthenticated && initialized) {
      fetchAnalytics();
    }
  }, [isAuthenticated, token, initialized, selectedCurrency]);

  // Fetch hot money analytics (solo se isPaying)
  useEffect(() => {
    const fetchHotMoneyAnalytics = async () => {
      if (!isAuthenticated || !initialized || !user?.organization?.isPaying) return;

      setHotMoneyAnalyticsLoading(true);
      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        const response = await axios.get('/api/requests/analytics/allocations', {
          params: { currency: selectedHotMoneyCurrency, requestType: 'hot_money' }
        });

        setHotMoneyAnalytics(response.data);

        if (response.data.availableCurrencies?.length > 0) {
          setAvailableHotMoneyCurrencies(response.data.availableCurrencies);
        }

        if (
          !hasAutoSwitchedHotMoneyCurrency.current &&
          response.data.totalAllocated === 0 &&
          response.data.availableCurrencies?.length > 0 &&
          !response.data.availableCurrencies.includes(selectedHotMoneyCurrency)
        ) {
          hasAutoSwitchedHotMoneyCurrency.current = true;
          setSelectedHotMoneyCurrency(response.data.availableCurrencies[0]);
        } else {
          hasAutoSwitchedHotMoneyCurrency.current = true;
        }

        setHotMoneyAnalyticsLoading(false);
      } catch (err) {
        console.error('Hot money analytics fetch error:', err);
        setHotMoneyAnalyticsLoading(false);
      }
    };

    if (isAuthenticated && initialized) {
      fetchHotMoneyAnalytics();
    }
  }, [isAuthenticated, token, initialized, selectedHotMoneyCurrency, user?.organization?.isPaying]);

  const isPaying = user?.organization?.isPaying;

  // Helper function to translate status
  const getStatusTranslation = (status) => {
    switch (status) {
      case 'bidding':
        return t('status.bidding');
      case 'pending_allocation':
        return t('status.pending');
      case 'completed':
        return t('status.completed');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return status.replace('_', ' ');
    }
  };

  // Helper to render the request type badge (deposit / hot money / VAT credit)
  const getRequestTypeBadge = (request) => {
    if (request.requestKind === 'vat_credit') {
      return {
        style: { backgroundColor: '#eef1f6', color: '#5a6b82' },
        label: t('requests.vatCredit')
      };
    }
    if (request.requestType === 'hot_money') {
      return {
        style: { backgroundColor: '#f8dfb0', color: '#8a5300' },
        label: t('requests.hotMoney')
      };
    }
    return {
      style: { backgroundColor: '#cfe3fb', color: '#0d47a1' },
      label: t('requests.allocation')
    };
  };

  // Colors for pie charts - more distinguishable blue/gray scale
  const COLORS = ['#1a73e8', '#5c9ce6', '#2e7d32', '#78909c', '#f4a742', '#607d8b', '#8e44ad', '#95a5a6', '#3498db', '#7f8c8d'];

  // Shared tooltip style so all pie charts read as one system
  const pieTooltipStyle = {
    borderRadius: '8px',
    border: '1px solid rgba(11,11,11,0.10)',
    boxShadow: '0 2px 8px rgba(11,11,11,0.12)',
    fontSize: '13px',
    padding: '8px 12px'
  };
  const pieLegendStyle = { fontSize: '13px', paddingTop: '12px' };

  // Filter data to exclude items with percentage less than 1%
  const filterSmallPercentages = (data) => {
    if (!data || data.length === 0) return [];
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return [];
    return data.filter(item => (item.value / total) * 100 >= 1);
  };

  // Custom label for pie charts showing percentage outside the donut
  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
    const percentage = (percent * 100).toFixed(0);

    // Don't show label if percentage is 0%
    if (percentage === '0') {
      return null;
    }

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25; // Position outside the donut
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#52514e"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="13"
        fontWeight="600"
      >
        {`${percentage}%`}
      </text>
    );
  };

  // Mobile Card Component for Active Requests
  const MobileActiveRequestCard = ({ request }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          #{request.requestId}
        </div>
        <div className="mobile-card-status" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span className={`status status-${request.status}`}>
            {getStatusTranslation(request.status)}
          </span>
        </div>
      </div>
      
      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.dateCreated')}</span>
          <span className="mobile-card-value">{new Date(request.createdAt).toLocaleDateString()}</span>
        </div>
        
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.amount')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
        </div>
        
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.bidsReceived')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.totalActiveBidAmount, request.currency || 'EUR')}</span>
        </div>
        
        {user?.organization?.isPaying && (
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.type')}</span>
            <span className="mobile-card-value">
              <span className="status" style={getRequestTypeBadge(request).style}>
                {getRequestTypeBadge(request).label}
              </span>
            </span>
          </div>
        )}

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.maturity')}</span>
          <span className="mobile-card-value">
            {request.requestKind === 'vat_credit' ? '-' : formatDuration(request.duration, t)}
          </span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date">
          {/* Spazio per eventuali info aggiuntive */}
        </div>
        <div className="mobile-card-action">
          <Link
            to={request.requestKind === 'vat_credit'
              ? `/company/vat-credit/requests/${request.id}`
              : `/company/requests/${request.id}`}
            className="view-btn"
          >
            {t('common.view')}
          </Link>
        </div>
      </div>
    </div>
  );
  
  if (loading) {
    return <div className="loading">{t('common.loadingDashboard')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="company-dashboard">
      {/* Active Requests Section */}
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <TrendingUp className="card-title-icon" size={20} />
          <span>{t('dashboard.activeRequests')}</span>
        </div>

        {/* Desktop Table */}
        {!isMobile && (
          <table>
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('requests.dateCreated')}</th>
                <th>{t('requests.amount')}</th>
                <th>{t('bids.bidsReceived')}</th>
                <th>{t('requests.maturity')}</th>
                {isPaying && <th>{t('requests.type')}</th>}
                <th>{t('bids.status')}</th>
                <th>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {activeRequests.length > 0 ? (
                activeRequests.map(request => (
                  <tr key={`${request.requestKind || 'liquidity'}-${request.id}`}>
                    <td>#{request.requestId}</td>
                    <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                    <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                    <td>{formatCurrencyWithSymbol(request.totalActiveBidAmount, request.currency || 'EUR')}</td>
                    <td>{request.requestKind === 'vat_credit' ? '-' : formatDuration(request.duration, t)}</td>
                    {isPaying && (
                      <td>
                        <span className="status" style={getRequestTypeBadge(request).style}>
                          {getRequestTypeBadge(request).label}
                        </span>
                      </td>
                    )}
                    <td>
                      <span className={`status status-${request.status}`}>
                        {getStatusTranslation(request.status)}
                      </span>
                    </td>
                    <td>
                      <Link
                        to={request.requestKind === 'vat_credit'
                          ? `/company/vat-credit/requests/${request.id}`
                          : `/company/requests/${request.id}`}
                        className="view-btn"
                      >
                        {t('common.view')}
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isPaying ? 8 : 7}>{t('dashboard.noActiveRequests')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Mobile Cards */}
        {isMobile && (
          <div className="mobile-cards-container">
            {activeRequests.length > 0 ? (
              activeRequests.map(request => (
                <MobileActiveRequestCard key={`${request.requestKind || 'liquidity'}-${request.id}`} request={request} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('dashboard.noActiveRequests')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Allocation Analytics 2026 Section */}
      <div className="card">
        <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PieChartIcon className="card-title-icon" size={20} />
            <span>{t('dashboard.totalAllocation2026', { defaultValue: 'Allocazione Totale 2026' })}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="currency-selector" style={{ fontSize: '14px', fontWeight: '500' }}>
              {t('dashboard.currency', { defaultValue: 'Valuta' })}:
            </label>
            <select
              id="currency-selector"
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {(availableCurrencies.length > 0 ? availableCurrencies : ['EUR']).map(cur => (
                <option key={cur} value={cur}>{cur}</option>
              ))}
            </select>
          </div>
        </div>

        {analyticsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {t('common.loading', { defaultValue: 'Caricamento...' })}
          </div>
        ) : (
          <>
            {/* Total Allocated */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '30px', marginTop: '20px' }}>
              <div style={{ textAlign: 'left', fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#767e8e', marginBottom: '0px' }}>
                {t('dashboard.totalAllocated', { defaultValue: 'Totale Allocato' })}
              </div>
              <div style={{ textAlign: 'left', fontSize: '32px', fontWeight: 'bold', color: '#1a73e8' }}>
                {formatCurrencyWithSymbol(Math.round(analytics.totalAllocated), selectedCurrency)}
              </div>
            </div>

            {analytics.totalAllocated > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '30px',
                marginTop: '20px'
              }}>
                {/* Bank Distribution Chart */}
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    {t('dashboard.allocationByBank', { defaultValue: 'Allocazione per Banca' })}
                  </h3>
                  {analytics.byBank.length > 0 && filterSmallPercentages(analytics.byBank).length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={filterSmallPercentages(analytics.byBank)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomLabel}
                          innerRadius={85}
                          outerRadius={115}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {filterSmallPercentages(analytics.byBank).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrencyWithSymbol(value, selectedCurrency)}
                          contentStyle={pieTooltipStyle}
                        />
                        <Legend iconType="circle" iconSize={9} wrapperStyle={pieLegendStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      {t('dashboard.noData', { defaultValue: 'Nessun dato disponibile' })}
                    </div>
                  )}
                </div>

                {/* Duration Distribution Chart */}
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}>
                    {t('dashboard.allocationByDuration', { defaultValue: 'Allocazione per Strumento' })}
                  </h3>
                  {analytics.byDuration.length > 0 && filterSmallPercentages(analytics.byDuration).length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={filterSmallPercentages(analytics.byDuration)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomLabel}
                          innerRadius={85}
                          outerRadius={115}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {filterSmallPercentages(analytics.byDuration).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrencyWithSymbol(value, selectedCurrency)}
                          contentStyle={pieTooltipStyle}
                        />
                        <Legend iconType="circle" iconSize={9} wrapperStyle={pieLegendStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      {t('dashboard.noData', { defaultValue: 'Nessun dato disponibile' })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                {t('dashboard.noAllocations2026', { defaultValue: 'Nessuna allocazione nel 2026 per questa valuta' })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Hot Money Analytics Section — solo per paying con dati */}
      {isPaying && (hotMoneyAnalytics.totalAllocated > 0 || hotMoneyAnalyticsLoading) && (
        <div className="card">
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PieChartIcon className="card-title-icon" size={20} style={{ color: '#e65100' }} />
              <span style={{ color: '#e65100' }}>{t('dashboard.totalHotMoney2026', { defaultValue: 'Hot Money 2026' })}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="hm-currency-selector" style={{ fontSize: '14px', fontWeight: '500' }}>
                {t('dashboard.currency', { defaultValue: 'Valuta' })}:
              </label>
              <select
                id="hm-currency-selector"
                value={selectedHotMoneyCurrency}
                onChange={(e) => setSelectedHotMoneyCurrency(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px', cursor: 'pointer' }}
              >
                {(availableHotMoneyCurrencies.length > 0 ? availableHotMoneyCurrencies : ['EUR']).map(cur => (
                  <option key={cur} value={cur}>{cur}</option>
                ))}
              </select>
            </div>
          </div>

          {hotMoneyAnalyticsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              {t('common.loading', { defaultValue: 'Caricamento...' })}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '30px', marginTop: '20px' }}>
                <div style={{ textAlign: 'left', fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#767e8e', marginBottom: '0px' }}>
                  {t('dashboard.totalAllocated', { defaultValue: 'Totale Allocato' })}
                </div>
                <div style={{ textAlign: 'left', fontSize: '32px', fontWeight: 'bold', color: '#e65100' }}>
                  {formatCurrencyWithSymbol(Math.round(hotMoneyAnalytics.totalAllocated), selectedHotMoneyCurrency)}
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '30px',
                marginTop: '20px'
              }}>
                {/* Per Banca */}
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', textAlign: 'center' }}>
                    {t('dashboard.allocationByBank', { defaultValue: 'Allocazione per Banca' })}
                  </h3>
                  {filterSmallPercentages(hotMoneyAnalytics.byBank).length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={filterSmallPercentages(hotMoneyAnalytics.byBank)}
                          cx="50%" cy="50%"
                          labelLine={false}
                          label={renderCustomLabel}
                          innerRadius={85} outerRadius={115}
                          dataKey="value"
                        >
                          {filterSmallPercentages(hotMoneyAnalytics.byBank).map((_, index) => (
                            <Cell key={`cell-hm-bank-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrencyWithSymbol(value, selectedHotMoneyCurrency)} contentStyle={pieTooltipStyle} />
                        <Legend iconType="circle" iconSize={9} wrapperStyle={pieLegendStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      {t('dashboard.noData', { defaultValue: 'Nessun dato disponibile' })}
                    </div>
                  )}
                </div>

                {/* Per Durata */}
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', textAlign: 'center' }}>
                    {t('dashboard.allocationByDuration', { defaultValue: 'Allocazione per Strumento' })}
                  </h3>
                  {filterSmallPercentages(hotMoneyAnalytics.byDuration).length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={filterSmallPercentages(hotMoneyAnalytics.byDuration)}
                          cx="50%" cy="50%"
                          labelLine={false}
                          label={renderCustomLabel}
                          innerRadius={85} outerRadius={115}
                          dataKey="value"
                        >
                          {filterSmallPercentages(hotMoneyAnalytics.byDuration).map((_, index) => (
                            <Cell key={`cell-hm-dur-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrencyWithSymbol(value, selectedHotMoneyCurrency)} contentStyle={pieTooltipStyle} />
                        <Legend iconType="circle" iconSize={9} wrapperStyle={pieLegendStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      {t('dashboard.noData', { defaultValue: 'Nessun dato disponibile' })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

    {/* Support Contact Section */}
      <div className="card" style={{ textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          <Mail size={16} style={{ color: '#6c757d' }} />
          <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>
            {t('dashboard.needHelp', { defaultValue: 'Hai bisogno di aiuto?' })}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
          {t('dashboard.contactSupport', { defaultValue: 'Contatta il nostro supporto:' })}{' '}
          <a href="mailto:support@euracle.it" style={{ color: '#1a73e8', textDecoration: 'none' }}>
            support@euracle.it
          </a>
        </p>
      </div>
    </div>
  );
};

export default CompanyDashboard;